import { useCallback, useEffect, useRef, useState } from 'react'
import { Transition } from 'react-transition-group'

import apple from '@/assets/lottie/apple.lottie?url'
// import squirrel from "@/assets/lottie/squirrel.lottie?url"
import brain_freeze from '@/assets/lottie/brain-freeze.lottie?url'
import brocoli from '@/assets/lottie/brocoli.lottie?url'
import carrot from '@/assets/lottie/carrot.lottie?url'
import clown from '@/assets/lottie/clown.lottie?url'
import confusion from '@/assets/lottie/confusion.lottie?url'
import dog from '@/assets/lottie/dog.lottie?url'
import mirror from '@/assets/lottie/mirror.lottie?url'
import palpitations from '@/assets/lottie/palpitations.lottie?url'
import pizza from '@/assets/lottie/pizza.lottie?url'
import sock from '@/assets/lottie/sock.lottie?url'
import spider_lane from '@/assets/lottie/spider-lane.lottie?url'
import spider_top from '@/assets/lottie/spider-top.lottie?url'
import television from '@/assets/lottie/television.lottie?url'
import { useAppState } from '@/composables/useAppState'
import { useGSAP } from '@/composables/useGSAP'
import { useImageFormatContext } from '@/composables/useImageFormatContext'
import { useRunnerState } from '@/composables/useRunnerState'
import useTracking from '@/composables/useTracking'
import { SFXAudio } from '@/config/AudioConfig'
import { getContentfulUrl } from '@/utils/contentful'
import AudioEngine from '@/webgl/audio/AudioEngine'

import LottieOrImage from '../Animation/LottieOrImage'

const Lotties: Record<string, unknown> = {
  dog,
  // squirrel,
  television,
  sock,
  pizza,
  carrot,
  brocoli,
  apple,
  clown,
  palpitations,
  brain_freeze,
  spider_top,
  spider_lane,
  mirror,
  confusion,
}

type RunnerEventProps = {
  id: string
  visible: boolean
}

const Images: Record<string, string> = {}

const Audio: Record<string, string> = {
  television: SFXAudio.TV,
  dog: SFXAudio.Dog,
  spider_top: SFXAudio.Spiders,
  spider_lane: SFXAudio.Spiders,
  squirrel: SFXAudio.Squirrel,
  clown: SFXAudio.Clown,
  mirror: SFXAudio.Mirror,
  carrot: SFXAudio.FunkyVeggies,
  apple: SFXAudio.FunkyVeggies,
  brocoli: SFXAudio.FunkyVeggies,
  brain_freeze: SFXAudio.Freeze,
  confusion: SFXAudio.RightLeft,
  palpitations: SFXAudio.Heart,
}

export default function RunnerEvent({ id, visible }: RunnerEventProps) {
  const { trackImpression } = useTracking()
  const { persona, scenarioUnitName } = useAppState()
  const { gameState } = useRunnerState()
  const nodeRef = useRef<HTMLDivElement>(null)

  const { gsap } = useGSAP()
  const [imgSrc, setImgSrc] = useState<string>(null)
  const [lottieData, setLottieData] = useState<unknown>(null)
  const [lottieFallbackUrl, setLottieFallbackUrl] = useState<string>(null)
  const [isLeaving, setIsLeaving] = useState(false)

  const enter = useCallback(() => {
    trackImpression({
      unitName: `${persona}_simulation${scenarioUnitName}`,
      unitVariation: `scenario_auto_obstacle_${id}`,
      unitLocation: 'tyce',
    })

    setIsLeaving(false)

    if (Audio[id]) AudioEngine.play(Audio[id])

    if (nodeRef.current) {
      gsap.fromTo(nodeRef.current, { scale: 0.8 }, { scale: 1, duration: 1.2, ease: 'power2.out' })
    }
  }, [nodeRef, gsap, id, trackImpression, persona, scenarioUnitName])

  const exit = useCallback(() => {
    if (!nodeRef.current) {
      setImgSrc(null)
      setLottieData(null)
      setLottieFallbackUrl(null)
      return
    }
    setIsLeaving(true)
    gsap.fromTo(
      nodeRef.current,
      { opacity: 1, scale: 1 },
      {
        opacity: 0,
        scale: 0.8,
        duration: 0.5,
        ease: 'power2.out',
        onComplete: () => {
          setIsLeaving(false)
          setImgSrc(null)
          setLottieData(null)
          setLottieFallbackUrl(null)
        },
      }
    )
  }, [nodeRef, gsap])

  const betterFormat = useImageFormatContext()
  useEffect(() => {
    if (isLeaving) return
    if (!id) {
      return
    }

    setImgSrc(Images[id] ? getContentfulUrl(Images[id], { fm: betterFormat }) || '' : null)
    setLottieData(Lotties[id] ? Lotties[id] : null)
    setLottieFallbackUrl(getContentfulUrl(`/src/assets/lottie/${id}.png`, { fm: betterFormat }) || '')
  }, [id, isLeaving, betterFormat])

  return (
    <Transition
      mountOnEnter
      unmountOnExit
      nodeRef={nodeRef}
      in={visible}
      onEnter={enter}
      onExit={exit}
      timeout={{
        appear: 1200,
        enter: 1200,
        exit: 500,
      }}
    >
      <div ref={nodeRef} className='w-full h-full flex justify-center fixed z-80'>
        {lottieData && (
          <LottieOrImage
            className='w-full h-full'
            animationData={lottieData}
            imageUrl={lottieFallbackUrl}
            loop={false}
            appear={true}
          />
        )}
        {imgSrc && <img src={imgSrc} alt='' className='h-full w-auto p-58' />}
      </div>
    </Transition>
  )
}
